'use client';

import { useCallback, useEffect, useState } from 'react';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { Dialog } from '@frameio/vapor';
import { useActiveDocumentId } from '../../shared/contexts/ActiveHostEntitiesContext';
import {
  useCloseSequenceExportDialog,
  useSequenceExportDialogConfig,
  useSequenceExportDialogState,
} from '../contexts/SequenceExportContext';
import { useSequenceEventsSubscription } from '../gql';
import { useLastExportAssetData } from '../hooks/useLastExportAssetData';
import { SequenceExport } from './SequenceExport';

export const SequenceExportDialog = () => {
  const activeSequenceId = useActiveDocumentId();
  const { isOpen } = useSequenceExportDialogState();
  const close = useCloseSequenceExportDialog();
  const config = useSequenceExportDialogConfig();
  const lastExportAssetData = useLastExportAssetData();
  const hasPreviousExport = Boolean(lastExportAssetData);
  const [forceNewAssetMode, setForceNewAssetMode] = useState(false);
  const track = useTrackUnsafe();

  useEffect(() => {
    if (isOpen) {
      setForceNewAssetMode(config?.shouldForceNewAssetMode ?? false);
    }
  }, [isOpen, config?.shouldForceNewAssetMode]);

  useSequenceEventsSubscription({
    onData: (subscriptionData) => {
      const event = subscriptionData.data?.data?.sequenceEvents;
      const evtType = event?.__typename;

      if (evtType === 'SequenceActivatedPayload' && isOpen) {
        if (event?.sequenceId === activeSequenceId) {
          return;
        }
        close();
      }
    },
    context: {
      resolveWithUXP: true,
    },
  });

  const isVersionStackMode = hasPreviousExport && !forceNewAssetMode;

  const handleExportModeToggle = useCallback(() => {
    if (isVersionStackMode) {
      setForceNewAssetMode(true);
      return;
    }

    if (hasPreviousExport) {
      setForceNewAssetMode(false);
      track('new-version-button-clicked', {
        source: 'PrUXP',
      });
    }
  }, [isVersionStackMode, hasPreviousExport, track]);

  const handleDialogClose = () => {
    close();
    setForceNewAssetMode(false);
  };
  return (
    <Dialog
      isShowing={isOpen}
      onRequestClose={handleDialogClose}
      style={{ width: '415px' }}
    >
      <SequenceExport
        canAddToVersionStack={isVersionStackMode || !!lastExportAssetData}
        isVersionStackMode={isVersionStackMode}
        onAddToVersionStack={handleExportModeToggle}
        onDismiss={handleDialogClose}
        overrideFolderId={config?.overrideFolderId}
      />
    </Dialog>
  );
};
